import React from "react";
import { useTranslation } from "react-i18next";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";
import { Action, Mode } from "../types";

type Props = {
    email: string;
    loading: boolean;
    error: string | null;
    dispatch: React.Dispatch<Action>;
    onDismiss?: () => void;
    onSubmit: (event: React.FormEvent) => void;
    theme: "light" | "dark";
    mode: Mode;
};

const STYLES = {
    light: {
        input: "w-full h-11 bg-white/80 border border-current/20 rounded-xl pl-10 pr-4 text-sm placeholder:opacity-30 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all",
        submit: "flex-1 h-10 rounded-xl bg-blue-900 text-white font-changa-one text-xs tracking-[0.12em] uppercase cursor-pointer hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
        secondary: "flex-1 h-10 rounded-xl bg-white/20 font-changa-one text-xs tracking-[0.12em] uppercase cursor-pointer hover:bg-white/30 transition-colors",
    },
    dark: {
        input: "w-full h-11 bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 text-white text-sm placeholder:text-white/30 outline-none focus:border-white/25 transition-all",
        submit: "flex-1 h-10 rounded-xl bg-white text-gray-900 font-changa-one text-sm tracking-[0.12em] uppercase cursor-pointer hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
        secondary: "flex-1 h-10 rounded-xl bg-white/5 text-white/50 font-changa-one text-xs tracking-[0.12em] uppercase cursor-pointer hover:bg-white/10 transition-colors",
    },
};

const EmailStep = ({ email, loading, error, dispatch, onDismiss, onSubmit, theme, mode }: Props) => {
    const { t } = useTranslation();
    const styles = STYLES[theme];
    const submitLabel = mode === "login" ? t("signup.login_submit") : t("signup.submit");

    return (
        <form onSubmit={onSubmit} className="w-full flex flex-col gap-2">
            <div className="relative">
                <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 opacity-30 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <input
                    type="email"
                    required
                    value={email}
                    onChange={(event) => dispatch({ type: "SET_EMAIL", email: event.target.value })}
                    placeholder={t("signup.placeholder")}
                    autoComplete="email"
                    autoCapitalize="none"
                    className={styles.input}
                />
            </div>
            {error && (
                <p className="text-rose-400 text-[10px] text-center">{error}</p>
            )}
            <div className="flex items-center gap-2">
                {onDismiss && (
                    <ButtonHapticWrapper>
                        <button type="button" onClick={onDismiss} className={styles.secondary}>
                            {t("signup.later")}
                        </button>
                    </ButtonHapticWrapper>
                )}
                <ButtonHapticWrapper>
                    <button type="submit" disabled={loading || !email.trim()} className={styles.submit}>
                        {loading ? "..." : submitLabel}
                    </button>
                </ButtonHapticWrapper>
            </div>
        </form>
    );
};

export default EmailStep;
